import { useEffect, useState } from "react";
import { PawPrint, AlertTriangle, Sparkles, Trash2, FolderOpen, RefreshCw, ExternalLink, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Pet Travel Tool — per-breed, per-route checklist for flying with a pet. */
const inp = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary";
const lbl = "block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1";
const PET_TYPES = [["dog", "Dog"], ["cat", "Cat"], ["bird", "Bird"], ["rabbit", "Rabbit"], ["other", "Other"]];
const MODES = [["", "Not sure yet"], ["cabin", "In cabin (small pets)"], ["checked", "Checked baggage"], ["cargo", "Manifest cargo"]];

export default function PetTravel() {
  const { user } = useAuth();
  const [form, setForm] = useState({ pet_type: "dog", breed: "", weight_kg: "", origin: "",
    destination: "", airline: "", travel_mode: "", ai: true });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/pets/policies").then((r) => setPolicies(r.data.items)).catch(() => {}); }, []);

  const loadSaved = () => { if (user) api.get("/pets/checks").then((r) => setSaved(r.data.items)).catch(() => {}); };
  useEffect(loadSaved, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!form.destination.trim()) { toast.error("Where is your pet flying to?"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pets/checklist", {
        ...form, weight_kg: form.weight_kg ? Number(form.weight_kg) : null, travel_mode: form.travel_mode || null,
      }, { timeout: 90000 });
      setResult(data);
      loadSaved();
      toast.success(user ? "Checklist ready — saved to your account" : "Checklist ready — sign in to save your progress");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the checklist"); }
    setBusy(false);
  };

  const toggle = async (sid, item) => {
    const done = !item.done;
    setResult((r) => ({ ...r, sections: r.sections.map((s) => s.key !== sid ? s
      : { ...s, items: s.items.map((i) => (i.id === item.id ? { ...i, done } : i)) }) }));
    if (result.id) { try { await api.put(`/pets/checks/${result.id}/items/${item.id}`, { done }); } catch {} }
  };

  const removeSaved = async (cid) => {
    try { await api.delete(`/pets/checks/${cid}`); setSaved((l) => l.filter((c) => c.id !== cid));
      if (result?.id === cid) setResult(null); toast.success("Checklist deleted"); } catch { toast.error("Could not delete"); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.post("/pets/checklist/pdf", result, { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pet_checklist_${(result.breed || result.pet_type || "pet").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Checklist PDF downloaded — bring it to the vet and the check-in counter");
    } catch { toast.error("Could not generate the PDF"); }
    setPdfBusy(false);
  };

  return (
    <div className="min-h-screen bg-zinc-50 py-10">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
          <PawPrint className="w-10 h-10 text-amber-600" /> Pet Travel Checklist
        </h1>
        <p className="text-muted-foreground mt-2 mb-8 text-sm">
          Flying with a dog, cat or other pet? Get a checklist tailored to the breed, weight and route —
          including snub-nosed breed warnings and strict-quarantine countries.
        </p>

        <div className="bg-white border border-zinc-200 mb-8">
          <div className="h-1 bg-amber-500" />
          <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pet-form">
            <div><label className={lbl}>Pet</label>
              <select value={form.pet_type} onChange={(e) => set("pet_type", e.target.value)} className={inp} data-testid="pet-type">
                {PET_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <div><label className={lbl}>Breed</label>
              <input value={form.breed} onChange={(e) => set("breed", e.target.value)} className={inp}
                placeholder="e.g. French Bulldog, Siamese" data-testid="pet-breed" /></div>
            <div><label className={lbl}>Weight (kg, with carrier)</label>
              <input type="number" min="0" step="0.5" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)}
                className={inp} placeholder="e.g. 6" data-testid="pet-weight" /></div>
            <div><label className={lbl}>From (country / city)</label>
              <input value={form.origin} onChange={(e) => set("origin", e.target.value)} className={inp}
                placeholder="e.g. United States" data-testid="pet-origin" /></div>
            <div><label className={lbl}>To (country / city) *</label>
              <input value={form.destination} onChange={(e) => set("destination", e.target.value)} className={inp}
                placeholder="e.g. Australia" data-testid="pet-destination" /></div>
            <div><label className={lbl}>Airline (optional)</label>
              <input value={form.airline} onChange={(e) => set("airline", e.target.value)} className={inp}
                placeholder="e.g. Lufthansa" data-testid="pet-airline" /></div>
            <div><label className={lbl}>Travel mode</label>
              <select value={form.travel_mode} onChange={(e) => set("travel_mode", e.target.value)} className={inp} data-testid="pet-mode">
                {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <label className="inline-flex items-end gap-2 text-sm pb-2 cursor-pointer" data-testid="pet-ai-toggle">
              <input type="checkbox" checked={form.ai} onChange={(e) => set("ai", e.target.checked)} />
              <span className="inline-flex items-center gap-1"><Sparkles className="w-4 h-4 text-violet-600" /> AI route tips</span>
            </label>
            <div className="flex items-end">
              <button onClick={generate} disabled={busy} data-testid="pet-generate"
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-600 text-white text-sm font-700 hover:bg-amber-700 disabled:opacity-50">
                {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PawPrint className="w-4 h-4" />}
                {busy ? "Building…" : "Build my pet checklist"}
              </button>
            </div>
          </div>
        </div>

        {result && (
          <div data-testid="pet-result">
          <div className="flex justify-end mb-2">
            <button onClick={downloadPdf} disabled={pdfBusy} data-testid="pet-pdf-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-600 text-amber-700 text-sm font-700 hover:bg-amber-50 disabled:opacity-50">
              {pdfBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              {pdfBusy ? "Preparing…" : "Print / PDF for vet & check-in"}
            </button>
          </div>
          {result.airline_policy && (
            <a href={result.airline_policy.url} target="_blank" rel="noopener noreferrer"
              data-testid="pet-airline-policy-link"
              className="flex items-start gap-2.5 border border-blue-300 bg-blue-50 text-blue-800 p-3.5 mb-2 text-sm hover:bg-blue-100 transition-colors">
              <ExternalLink className="w-4 h-4 mt-0.5 shrink-0" />
              <span><b>Official {result.airline_policy.name} pet policy</b> — read the airline's own rules
                for breeds, carriers and fees before booking.
                {result.airline_policy.note && <> {result.airline_policy.note}.</>}</span>
            </a>
          )}
            {result.warnings.map((w, i) => (
              <div key={i} className={`border p-3.5 mb-2 flex items-start gap-2.5 text-sm ${w.level === "high"
                ? "border-rose-300 bg-rose-50 text-rose-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}
                data-testid={`pet-warning-${i}`}>
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {w.text}
              </div>
            ))}
            {result.ai_notes?.length > 0 && (
              <div className="border border-violet-200 bg-violet-50/60 p-4 my-4" data-testid="pet-ai-notes">
                <div className="text-sm font-800 text-violet-700 flex items-center gap-1.5 mb-2"><Sparkles className="w-4 h-4" /> Route tips (AI — verify anything regulatory)</div>
                <ul className="text-sm text-zinc-700 space-y-1 list-disc ml-5">{result.ai_notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
              </div>
            )}
            <div className="space-y-4 mt-4">
              {result.sections.map((s) => {
                const done = s.items.filter((i) => i.done).length;
                return (
                  <div key={s.key} className="bg-white border border-zinc-200" data-testid={`pet-section-${s.key}`}>
                    <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between">
                      <h3 className="font-heading font-900">{s.title}</h3>
                      <span className="text-xs font-mono text-muted-foreground">{done}/{s.items.length}</span>
                    </div>
                    <ul className="p-4 space-y-2">
                      {s.items.map((i) => (
                        <li key={i.id}>
                          <label className="flex items-start gap-2.5 text-sm cursor-pointer group">
                            <input type="checkbox" checked={i.done} onChange={() => toggle(s.key, i)}
                              className="mt-0.5" data-testid={`pet-item-${i.id}`} />
                            <span className={i.done ? "line-through text-muted-foreground" : "text-zinc-800"}>{i.text}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              General guidance only — airline pet policies and country import rules change often. Always confirm
              with your airline and the destination's official government source before booking.
            </p>
          </div>
        )}

        {policies.length > 0 && (
          <details className="mt-10 bg-white border border-zinc-200" data-testid="pet-policy-directory">
            <summary className="px-4 py-3 cursor-pointer font-heading font-900 text-sm hover:bg-zinc-50">
              Official airline pet policies — quick directory ({policies.length} airlines)
            </summary>
            <div className="px-4 pb-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
              {policies.map((p) => (
                <a key={p.iata} href={p.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`pet-policy-${p.iata}`}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline py-0.5">
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {p.name}
                  {p.note && <span className="text-[10px] text-rose-600 font-700">(restricted)</span>}
                </a>
              ))}
            </div>
            <p className="px-4 pb-3 text-xs text-muted-foreground">Links go to each airline's own website — policies change, always check before booking.</p>
          </details>
        )}

        {user && saved.length > 0 && (
          <div className="mt-10" data-testid="pet-saved-list">
            <h2 className="font-heading font-900 text-lg mb-3">My saved pet checklists</h2>
            {saved.map((c) => {
              const total = c.sections.reduce((a, s) => a + s.items.length, 0);
              const done = c.sections.reduce((a, s) => a + s.items.filter((i) => i.done).length, 0);
              return (
                <div key={c.id} className="border border-zinc-200 bg-white p-3 mb-2 flex items-center gap-3 text-sm" data-testid={`pet-saved-${c.id}`}>
                  <PawPrint className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="font-700">{c.breed || c.pet_type}</span>
                  <span className="text-muted-foreground truncate">{[c.origin, c.destination].filter(Boolean).join(" → ")}</span>
                  <span className="ml-auto text-xs font-mono text-muted-foreground shrink-0">{done}/{total}</span>
                  <button onClick={() => setResult(c)} data-testid={`pet-open-${c.id}`}
                    className="inline-flex items-center gap-1 text-xs font-700 text-primary hover:underline shrink-0"><FolderOpen className="w-3.5 h-3.5" /> Open</button>
                  <button onClick={() => removeSaved(c.id)} data-testid={`pet-delete-${c.id}`}
                    className="text-zinc-300 hover:text-rose-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
